"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import type { ReactNode } from "react";
import { Language, getTranslations } from "./translations";
import {
  buildLanguageCookie,
  matchLanguageFromTags,
  parseLanguage,
  readLanguageCookie,
} from "./language-preference";

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: ReturnType<typeof getTranslations>;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function detectBrowserLanguage(): Language {
  if (typeof navigator === "undefined") return "es";
  const candidates = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];
  return matchLanguageFromTags(candidates) ?? "es";
}

type LanguageProviderProps = {
  children: ReactNode;
  /** From server (cookie or Accept-Language) so SSR and first client render match. */
  initialLanguage?: Language;
};

export function LanguageProvider({
  children,
  initialLanguage = "es",
}: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(initialLanguage);

  // After hydration: cookie/SSR wins over stale localStorage; navigator only if no saved pref.
  useEffect(() => {
    const cookieLang = readLanguageCookie();
    const savedLs = parseLanguage(localStorage.getItem("language"));

    let resolved: Language = cookieLang ?? initialLanguage;

    if (!cookieLang && !savedLs) {
      const detected = detectBrowserLanguage();
      if (detected !== resolved) {
        resolved = detected;
      }
    }

    if (resolved !== language) {
      setLanguageState(resolved);
    }

    localStorage.setItem("language", resolved);

    if (!cookieLang) {
      document.cookie = buildLanguageCookie(resolved);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== "undefined") {
      localStorage.setItem("language", lang);
      document.cookie = buildLanguageCookie(lang);
    }
  }, []);

  const t = useMemo(() => getTranslations(language), [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
    }),
    [language, setLanguage, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
